import { CommonModule } from '@angular/common';
import { Component, ChangeDetectorRef } from '@angular/core';
import { ReactiveFormsModule, FormsModule, Validators } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Subscription, Subject, debounceTime, tap, catchError, finalize } from 'rxjs';
import { getIdFormConfig } from '../../../../../../views/pages/adminstration/temp-common/utils';
import { ErrorOrError, handleApiErrors } from '../../../../../core';
import {
  IUpdateSpecializedRolesRequest,
  IUpdateSpecializedRolesResponse,
  ICreateSpecializedRolesRequest,
  ICreateSpecializedRolesResponse
} from '../../dtos';
import { SpecializedRolesService } from '../../services';
import { FormComponent, ToastService } from '../../../../../../trash/shared/components';
import { FormInputConfig, AllInputTypes } from '../../../../../../trash/shared/interfaces/form-input-config';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-create-update-specialized-roles',
  standalone: true,
  imports: [ReactiveFormsModule, CommonModule, DialogModule, ButtonModule, FormsModule, FormComponent, TranslateModule],
  templateUrl: './create-update-specialized-roles.component.html',
  styleUrl: './create-update-specialized-roles.component.scss'
})
export class CreateUpdateSpecializedRolesComponent {
  private subscriptions: Subscription[] = [];

  isLoadingAddEditItem = false;
  form: FormInputConfig[] = [];
  formValues: any = null;
  specializedRolesId: string | undefined;
  validationErrors: any = {};
  validationEmit = false;
  systemRole: any = [];
  selectedSystemRoleItem: any = [];
  private searchSubject = new Subject<any>();

  constructor(
    private specializedRolesService: SpecializedRolesService,
    public dataObj: DynamicDialogConfig,
    private toastService: ToastService,
    private cdr: ChangeDetectorRef,
    private ref: DynamicDialogRef,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.specializedRolesId = this.dataObj?.data?.item?.id;
    if (this.dataObj?.data?.item) {
      this.formValues = this.dataObj?.data?.item;
    }

    this.initForm();
    this.getAllSystemRoles();

    this.searchSubject.pipe(debounceTime(1000)).subscribe((field: any) => {
      if (field.keyword.length >= 2 || field.keyword.length == 0) {
        if (field?.fieldName == 'systemRoleId') {
          this.getAllSystemRoles(field?.keyword);
          this.selectedSystemRoleItem = this.selectedSystemRoleItem.filter(
            (value: any, index: any, self: any) => index === self.findIndex((t: any) => t.value === value.value)
          );
        }
      }
    });
  }

  onFilterSelect(e: any) {
    this.searchSubject.next(e);
  }

  // Initialize the form
  initForm() {
    this.form = [
      getIdFormConfig(this.specializedRolesId),
      {
        type: AllInputTypes.Text,
        name: 'name',
        label: this.translate.instant('GENERAL.NAME'),
        placeholder: `${this.translate.instant('GENERAL.ENTER')} ${this.translate.instant('GENERAL.NAME')}`,
        defaultValue: this.formValues?.englishName,
        isDisable: !this.dataObj?.data?.visible,
        widthClass: 'col-12',
        validation: [{ function: Validators.required, errorMessage: `${this.translate.instant('GENERAL.PLEASE_ENTER')} ${this.translate.instant('GENERAL.NAME')}` }]
      },
      {
        type: AllInputTypes.Select,
        name: 'systemRoleId',
        label: this.translate.instant('USER_MANAGEMENT.SYSTEM_ROLES'),
        placeholder: this.translate.instant('GENERAL.SELECT'),
        widthClass: 'col-md-12',
        listValues: [],
        defaultValue: this.formValues?.systemRole?.id,
        savedValue: this.formValues?.systemRole?.id,
        // defaultValue: Array.isArray(this.formValues?.systemRole)
        //   ? this.formValues?.systemRole.map((item: any) => item?.id)
        //   : [],
        // savedValue: Array.isArray(this.formValues?.systemRole)
        //   ? this.formValues?.systemRole.map((item: any) => item?.id)
        //   : [],
        isDisable: !!this.specializedRolesId || !this.dataObj?.data?.visible
      }
    ];
  }
  valueChanged(data: any) {
    this.formValues = { ...data };
  }

  emitValidation() {
    this.validationEmit = !this.validationEmit;
    this.cdr.detectChanges();
  }

  getAllSystemRoles(keyword: string = '') {
    let sysRolesSubscription: Subscription = this.specializedRolesService
      .getAllSystemRoles({ SearchKeyword: keyword })
      .pipe(
        tap((res: any) => this.processRolesResponse(res)),
        catchError((errors: ErrorOrError[]) => handleApiErrors(errors, this.toastService)),
        finalize(() => {})
      )
      .subscribe();

    this.subscriptions.push(sysRolesSubscription);
  }
  private processRolesResponse(response: any): void {
    if (response) {
      let items: any = response.items;
      this.systemRole = items?.map((item: any) => {
        return {
          label: item.englishName,
          value: item.id
        };
      });
      const newItems = this.selectedSystemRoleItem.filter(
        (el2: any) => !this.systemRole.find((el1: any) => el1.value === el2.value)
      );
      // Push the new items to systemRole
      this.systemRole.push(...newItems);
      this.updateFormConfig();
      this.cdr.markForCheck();
    }
  }

  private updateFormConfig(): void {
    const systemRoleField = this.form.find((field) => field.name === 'systemRoleId');
    if (systemRoleField) {
      systemRoleField.listValues = this.systemRole;
      // Update defaultValue if formValues are already available
      if (this.formValues?.systemRole?.id) {
        systemRoleField.defaultValue = this.formValues?.systemRole?.id;
      }
    }
  }

  async submitForm(data: any) {
    this.isLoadingAddEditItem = true;

    // Determine request type
    if (this.specializedRolesId) {
      // Handle update request
      const updateData: IUpdateSpecializedRolesRequest = this.prepareUpdateDataRequest(data);
      const subscription = (await this.specializedRolesService.update(updateData, this.specializedRolesId))
        .pipe(
          tap((res: IUpdateSpecializedRolesResponse) => {
            // Process response specific to update
            this.processAddEditResponse(res, true);
          }),
          catchError((errors: ErrorOrError[]) => handleApiErrors(errors, this.toastService)),
          finalize(() => {
            this.isLoadingAddEditItem = false;
            this.cdr.detectChanges();
          })
        )
        .subscribe();
      this.subscriptions.push(subscription);
    } else {
      // Handle create request
      const createData: ICreateSpecializedRolesRequest = this.prepareCreateDataRequest(data);
      const subscription = (await this.specializedRolesService.create(createData))
        .pipe(
          tap((res: ICreateSpecializedRolesResponse) => {
            // Process response specific to create
            this.processAddEditResponse(res, false);
          }),
          catchError((errors: ErrorOrError[]) => handleApiErrors(errors, this.toastService)),
          finalize(() => {
            this.isLoadingAddEditItem = false;
            this.cdr.detectChanges();
          })
        )
        .subscribe();
      this.subscriptions.push(subscription);
    }
  }
  private prepareCreateDataRequest(data: any): ICreateSpecializedRolesRequest {
    return {
      name: data?.name,
      systemRoleId: data?.systemRoleId
    };
  }

  private prepareUpdateDataRequest(data: any): IUpdateSpecializedRolesRequest {
    return {
      name: data?.name
    };
  }
  private processAddEditResponse(
    response: IUpdateSpecializedRolesResponse | ICreateSpecializedRolesResponse,
    isUpdate: boolean
  ) {
    if (isUpdate) {
      this.toastService.showSuccess(this.translate.instant('GENERAL.SUCCESS'), this.translate.instant('GENERAL.SUCCESSFULLY_UPDATED'));
    } else {
        this.toastService.showSuccess(this.translate.instant('GENERAL.SUCCESS'), this.translate.instant('GENERAL.SUCCESSFULLY_CREATED'));
    }
    this.onClose(true);
    this.cdr.detectChanges();
  }

  onClose(data?: any) {
    this.ref.close(data);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }
}
